// src/components/SurveillanceDetails.jsx
import React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Divider,
  Grid
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

dayjs.extend(utc);

const SurveillanceDetails = ({
  open,
  onClose,
  rowData
}) => {
  const handleClose = () => {
    onClose();
  };

  if (!rowData) {
    return null;
  }

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
      // Date-only field: render in the UTC frame so values do not shift a day
      // for clients west of UTC. 'M/D/YYYY' mirrors the prior locale default.
      return dayjs.utc(dateValue).format('M/D/YYYY');
    } catch (error) {
      return dateValue;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '300px'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        pb: 1
      }}>
        <Typography variant="h6">
          Security Details
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              {rowData.SECURITY || 'Security Name Not Available'}
            </Typography>
            <Divider />
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  CUSIP
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {rowData.CUSIP || 'N/A'}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  NAIC Designations
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {rowData.NAIC_DESIGNATIONS || 'N/A'}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Settle Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(rowData.SETTLE_DATE)}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Load ID
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {rowData.LOADID || 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Additional Information
            </Typography>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2">
                  <strong>Asset Class:</strong> {rowData.BROAD_ASSET_CLASS || 'N/A'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2">
                  <strong>Final Grouping:</strong> {rowData.FINAL_GROUPING || 'N/A'}
                </Typography>
              </Grid>
              {rowData.ASOFDT && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2">
                    <strong>As Of Date:</strong> {formatDate(rowData.ASOFDT)}
                  </Typography>
                </Grid>
              )}
              {rowData.MODIFIED_BY && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2">
                    <strong>Modified By:</strong> {rowData.MODIFIED_BY}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurveillanceDetails;
